import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { toast } from "sonner";
import {
  ChevronLeft, UserPlus, UserMinus, MessageCircle, Swords, Trophy,
  Loader2, Camera,
  Smartphone, Monitor,
} from "lucide-react";
import RelPill from "../RelPill";

/**
 * V2 / Mobile-native Player Profile.
 * Renders inside <MobileShell> at /v2/players/:username — keeps the dark
 * mobile UI and bottom nav, no break-out to V1.
 *
 * Reuses GET /players/:username and the follow/unfollow endpoints unchanged.
 */
export default function PlayerProfileV2() {
  const { username } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useT();

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [followBusy, setFollowBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/players/${encodeURIComponent(username)}`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(e?.response?.status === 404 ? "notfound" : "error");
      }
    })();
    return () => { alive = false; };
  }, [username]);

  if (err === "notfound") {
    return (
      <div className="px-5 pt-5">
        <BackBar onBack={() => nav(-1)} />
        <div className="mt-4 rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-6 text-center text-[#71717A] text-sm" data-testid="v2-player-notfound">
          {t("v2.player.notfound")}
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="px-5 pt-5">
        <BackBar onBack={() => nav(-1)} />
        <div className="mt-4 rounded-2xl bg-[#E11D48]/10 border border-[#E11D48]/30 p-4 text-sm text-[#FCA5A5]" data-testid="v2-player-error">
          {t("v2.player.error")}
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex items-center justify-center py-20 text-[#52525B]" data-testid="v2-player-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const p = data.user || {};
  const s = data.stats || {};
  const form = data.form || [];
  const isMe = user && user.id === p.id;

  const doToggleFollow = async () => {
    setFollowBusy(true);
    try {
      if (p.is_following) {
        await api.delete(`/users/${p.id}/follow`);
        setData((prev) => prev && { ...prev, user: {
          ...prev.user,
          is_following: false,
          is_friend: false,
          followers_count: Math.max(0, (prev.user.followers_count || 1) - 1),
        }});
      } else {
        await api.post(`/users/${p.id}/follow`);
        setData((prev) => prev && { ...prev, user: {
          ...prev.user,
          is_following: true,
          is_friend: prev.user.is_followed_by ? true : prev.user.is_friend,
          followers_count: (prev.user.followers_count || 0) + 1,
        }});
      }
    } catch { /* noop */ }
    finally { setFollowBusy(false); }
  };

  const onAvatarPick = async (file) => {
    if (!file || !isMe) return;
    if (file.size > 1024 * 1024) {
      toast.error(t("v2.player.avatar.tooLarge"));
      return;
    }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const { data: up } = await api.post("/users/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("v2.player.avatar.updated"));
      setData((prev) => prev && { ...prev, user: { ...prev.user, avatar_url: up.avatar_url } });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAvatarBusy(false);
    }
  };

  return (
    <div className="px-5 pt-5 pb-6 space-y-4" data-testid="v2-player-profile">
      <BackBar onBack={() => nav(-1)} />

      {/* FUT-style hero card */}
      <section className="relative rounded-3xl overflow-hidden border border-[#7C3AED]/40 shadow-2xl shadow-[#7C3AED]/30" data-testid="v2-player-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7C3AED] via-[#6D28D9] to-[#3B82F6]" />
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="relative p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[28px] font-bold text-white tabular-nums leading-none">{s.win_rate ?? 0}</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/70 mt-0.5">{t("v2.player.winRate")}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">{(p.platform || "mobile").toUpperCase()}</div>
              {p.reliability && (
                <div className="mt-1 inline-block">
                  <RelPill score={p.reliability.score} flagged={p.reliability.flagged} size="sm" />
                </div>
              )}
            </div>
          </div>

          {/* Avatar / initials (with upload-on-tap when self) */}
          <div className="mt-4 flex items-center justify-center" data-testid="v2-player-avatar-wrap">
            <div className="relative">
              {p.avatar_url ? (
                <img
                  src={p.avatar_url}
                  alt={p.username}
                  className="w-28 h-28 rounded-full object-cover ring-4 ring-white/25 shadow-2xl"
                  data-testid="v2-player-avatar"
                />
              ) : (
                <div
                  className="w-28 h-28 rounded-full bg-gradient-to-br from-white/30 to-white/10 flex items-center justify-center font-bold text-5xl text-white shadow-2xl ring-4 ring-white/25"
                  aria-hidden
                  data-testid="v2-player-avatar-initial"
                >
                  {(p.username || "?").charAt(0).toUpperCase()}
                </div>
              )}
              {isMe && (
                <>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => onAvatarPick(e.target.files?.[0])}
                    data-testid="v2-player-avatar-input"
                  />
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={avatarBusy}
                    title={p.avatar_url ? t("v2.player.avatar.replace") : t("v2.player.avatar.upload")}
                    data-testid="v2-player-avatar-edit"
                    className="absolute bottom-1 right-1 w-9 h-9 rounded-full bg-white text-[#7C3AED] flex items-center justify-center shadow-xl ring-2 ring-white/60 hover:scale-105 transition-transform disabled:opacity-50"
                  >
                    {avatarBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="mt-3 text-center">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-white truncate" data-testid="v2-player-username">{p.username}</h1>
              {p.is_friend && <span className="text-[#FFD700]" title="Mutual follow"><Trophy className="w-4 h-4" /></span>}
              {p.banned && <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#E11D48] text-white">banned</span>}
            </div>
            {p.club_name && (
              <div className="mt-0.5 text-xs text-white/80 truncate">{p.club_name}</div>
            )}
            {p.game_id && (
              <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.25em] text-white/60">{p.game_id}</div>
            )}
          </div>

          {/* Stat strip */}
          <div className="mt-4 grid grid-cols-3 gap-2 bg-black/30 backdrop-blur rounded-2xl p-3 border border-white/10" data-testid="v2-player-stat-strip">
            <StripStat label={t("v2.player.pts")} value={s.points ?? 0} accent="#10B981" />
            <StripStat label={t("v2.player.played")} value={s.played ?? 0} />
            <StripStat label={t("v2.player.wdl")} value={`${s.won ?? 0}-${s.drawn ?? 0}-${s.lost ?? 0}`} />
          </div>

          {/* Follower counts */}
          <div className="mt-3 flex items-center justify-center gap-5 text-[11px] text-white/80">
            <div data-testid="v2-player-followers">
              <span className="font-bold text-white">{p.followers_count ?? 0}</span> {t("v2.player.followers")}
            </div>
            <div data-testid="v2-player-following">
              <span className="font-bold text-white">{p.following_count ?? 0}</span> {t("v2.player.following")}
            </div>
          </div>
        </div>
      </section>

      {/* Action buttons */}
      {!isMe && user && (
        <section className="grid grid-cols-3 gap-2" data-testid="v2-player-actions">
          <ActionBtn
            Icon={p.is_following ? UserMinus : UserPlus}
            label={p.is_following ? t("v2.player.unfollow") : t("v2.player.follow")}
            onClick={doToggleFollow}
            disabled={followBusy}
            testId="v2-player-follow-btn"
            primary={!p.is_following}
          />
          <ActionBtn
            Icon={Swords}
            label={t("v2.player.h2h")}
            to={`/v2/h2h/${p.id}`}
            testId="v2-player-h2h-btn"
          />
          <ActionBtn
            Icon={MessageCircle}
            label={t("v2.player.message")}
            to={`/v2/chat/${p.id}`}
            testId="v2-player-message-btn"
          />
        </section>
      )}

      {/* Detailed stats */}
      <section className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-4" data-testid="v2-player-stats">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white">{t("v2.player.stats")}</h3>
          <div className="flex items-center gap-1.5">
            {(p.platform || "mobile") === "mobile" ? (
              <Smartphone className="w-3.5 h-3.5 text-[#A5B4FC]" />
            ) : (
              <Monitor className="w-3.5 h-3.5 text-[#A5B4FC]" />
            )}
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#A1A1AA]">{(p.platform || "mobile")}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <StatBlock label={t("v2.player.played")}  value={s.played ?? 0} />
          <StatBlock label={t("v2.player.won")}     value={s.won ?? 0}    accent="#10B981" />
          <StatBlock label={t("v2.player.drawn")}   value={s.drawn ?? 0}  accent="#F59E0B" />
          <StatBlock label={t("v2.player.lost")}    value={s.lost ?? 0}   accent="#EF4444" />
          <StatBlock label={t("v2.player.gf")}      value={s.gf ?? 0} />
          <StatBlock label={t("v2.player.ga")}      value={s.ga ?? 0} />
          <StatBlock label={t("v2.player.gd")}      value={`${(s.gd ?? 0) >= 0 ? "+" : ""}${s.gd ?? 0}`} accent={(s.gd ?? 0) >= 0 ? "#10B981" : "#EF4444"} />
          <StatBlock label={t("v2.player.points")}  value={s.points ?? 0} accent="#A5B4FC" />
          <StatBlock label={t("v2.player.seasons")} value={s.league_count ?? 0} />
        </div>

        {/* Form */}
        <div className="mt-3 rounded-xl bg-[#0F0F18] border border-[#1F1F2A] p-3" data-testid="v2-player-form">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717A] mb-2">{t("v2.player.form")}</div>
          {form.length === 0 ? (
            <div className="text-xs text-[#52525B] font-mono">—</div>
          ) : (
            <div className="flex gap-1.5 flex-wrap">
              {form.map((o, i) => <OutcomeChip key={i} outcome={o} />)}
            </div>
          )}
        </div>
      </section>

      {/* H2H section if present */}
      {Array.isArray(data.h2h) && data.h2h.length > 0 && (
        <section className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-4" data-testid="v2-player-h2h-history">
          <h3 className="text-sm font-bold text-white mb-3">{t("v2.player.h2hHistory")}</h3>
          <ul className="space-y-2">
            {data.h2h.slice(0, 10).map((m, i) => (
              <H2HRow key={m.match_id || i} m={m} t={t} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function BackBar({ onBack }) {
  const { t } = useT();
  return (
    <button
      type="button"
      onClick={onBack}
      data-testid="v2-player-back"
      className="inline-flex items-center gap-1.5 text-[#A1A1AA] hover:text-white text-xs font-mono uppercase tracking-[0.25em] transition-colors"
    >
      <ChevronLeft className="w-4 h-4" />
      {t("v2.match.back")}
    </button>
  );
}

function StripStat({ label, value, accent }) {
  return (
    <div className="text-center">
      <div className="text-base font-bold tabular-nums" style={{ color: accent || "#FFFFFF" }}>{value}</div>
      <div className="text-[9px] font-bold uppercase tracking-widest text-white/70 mt-0.5">{label}</div>
    </div>
  );
}

function StatBlock({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-[#0F0F18] border border-[#1F1F2A] px-3 py-2.5 text-center" data-testid={`v2-player-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-base font-bold tabular-nums" style={{ color: accent || "#FFFFFF" }}>{value}</div>
      <div className="text-[9px] font-bold uppercase tracking-widest text-[#71717A] mt-0.5">{label}</div>
    </div>
  );
}

function ActionBtn({ Icon, label, onClick, to, disabled, primary, testId }) {
  const cls = `rounded-2xl border px-2 py-3 inline-flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-50 ${
    primary
      ? "bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] border-transparent text-white shadow-lg shadow-[#7C3AED]/30 hover:opacity-95"
      : "bg-[#12121C] border-[#1F1F2A] text-white hover:border-[#7C3AED]"
  }`;
  const inner = (
    <>
      <Icon className="w-4 h-4" />
      <span className="text-[10px] font-bold uppercase tracking-widest truncate">{label}</span>
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testId}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={cls} data-testid={testId}>
      {inner}
    </button>
  );
}

function OutcomeChip({ outcome }) {
  const map = {
    W: { bg: "#10B98133", color: "#10B981" },
    D: { bg: "#F59E0B33", color: "#F59E0B" },
    L: { bg: "#EF444433", color: "#EF4444" },
  };
  const cfg = map[outcome] || { bg: "#52525B33", color: "#A1A1AA" };
  return (
    <span
      className="w-7 h-7 rounded-md inline-flex items-center justify-center text-xs font-bold"
      style={{ backgroundColor: cfg.bg, color: cfg.color }}
    >
      {outcome}
    </span>
  );
}

function H2HRow({ m, t }) {
  return (
    <li className="flex items-center gap-3 rounded-xl bg-[#0F0F18] border border-[#1F1F2A] px-3 py-2">
      <Link
        to={`/v2/matches/${m.match_id}`}
        className="flex items-center gap-3 flex-1 min-w-0"
        data-testid={`v2-player-h2h-row-${m.match_id}`}
      >
        <span className="text-[10px] font-mono text-[#71717A]">{m.scheduled_at || "—"}</span>
        <span className="flex-1 text-xs text-white truncate">{m.league_name || "—"}</span>
        <span className="text-sm font-bold text-white tabular-nums">{m.score_home}–{m.score_away}</span>
      </Link>
    </li>
  );
}
